// =====================================================================================
// INVRT Radar — web console — account — delivery — the "Add target" form
//
// POST /delivery/targets (contract §6). One dialog, five kinds, and for each kind exactly
// the `config` object the server's own schema expects (_shared/delivery/targets.ts):
//
//   email      {recipients: 1–10 addresses}
//   hubspot    {access_token, lifecycle_stage?}   private-app token, probed live
//   pipedrive  {api_token, company_domain}        probed live
//   invrt_crm  {endpoint_url (https), api_key}
//   monti      {endpoint_url (https), api_key}
//
// The credential fields are password inputs and the value leaves this component in the
// request body and nowhere else: on success the list is re-read, and what comes back is
// the server's masked `token_hint`. A failed live probe is the server's 422
// `target_credentials_rejected`, shown inline in plain words so the token can be corrected
// without leaving the form; every other failure goes through ErrorState.
//
// The body sends `site_id` explicitly (null = every site) and `events` explicitly, so what
// was chosen is what is stored rather than what the server would have defaulted to.
// =====================================================================================

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient, useSites } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { ErrorState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input, Select, Textarea } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { isApiError } from "@/lib/api";
import type {
  CreateDeliveryTargetRequest,
  DeliveryTarget,
  DeliveryTargetKind,
  HubspotTargetConfigWrite,
  WebhookEventKind,
} from "@/lib/types.c1";
import { EventKindCheckboxes } from "./EventKindCheckboxes";
import { DEFAULT_TARGET_EVENTS, TARGET_KIND_LABELS, TARGET_KINDS, targetsKey } from "./hooks";

const MAX_RECIPIENTS = 10;

const CREDENTIALS_REFUSED_COPY =
  "The credentials were refused by the destination. Check the token and try again.";

/** One address per line or comma-separated; blanks dropped, order kept, duplicates removed. */
function parseRecipients(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const piece of raw.split(/[\s,;]+/)) {
    const address = piece.trim();
    if (address === "" || seen.has(address.toLowerCase())) continue;
    seen.add(address.toLowerCase());
    out.push(address);
  }
  return out;
}

interface FormState {
  kind: DeliveryTargetKind;
  label: string;
  siteId: string;
  events: WebhookEventKind[];
  recipients: string;
  accessToken: string;
  lifecycleStage: string;
  apiToken: string;
  companyDomain: string;
  endpointUrl: string;
  apiKey: string;
}

const INITIAL: FormState = {
  kind: "email",
  label: "",
  siteId: "",
  events: DEFAULT_TARGET_EVENTS,
  recipients: "",
  accessToken: "",
  lifecycleStage: "",
  apiToken: "",
  companyDomain: "",
  endpointUrl: "",
  apiKey: "",
};

/** The request body for the current form, or null while something required is missing. */
function buildCreateBody(form: FormState): CreateDeliveryTargetRequest | null {
  const label = form.label.trim();
  if (label === "" || form.events.length === 0) return null;
  const common = {
    label,
    site_id: form.siteId === "" ? null : form.siteId,
    events: form.events,
  };

  switch (form.kind) {
    case "email": {
      const recipients = parseRecipients(form.recipients);
      if (recipients.length === 0 || recipients.length > MAX_RECIPIENTS) return null;
      return { kind: "email", ...common, config: { recipients } };
    }
    case "hubspot": {
      const accessToken = form.accessToken.trim();
      if (accessToken === "") return null;
      const config: HubspotTargetConfigWrite = { access_token: accessToken };
      const stage = form.lifecycleStage.trim();
      if (stage !== "") config.lifecycle_stage = stage;
      return { kind: "hubspot", ...common, config };
    }
    case "pipedrive": {
      const apiToken = form.apiToken.trim();
      const companyDomain = form.companyDomain.trim();
      if (apiToken === "" || companyDomain === "") return null;
      return {
        kind: "pipedrive",
        ...common,
        config: { api_token: apiToken, company_domain: companyDomain },
      };
    }
    case "invrt_crm":
    case "monti": {
      const endpointUrl = form.endpointUrl.trim();
      const apiKey = form.apiKey.trim();
      if (!/^https:\/\/\S+$/i.test(endpointUrl) || apiKey === "") return null;
      return {
        kind: form.kind,
        ...common,
        config: { endpoint_url: endpointUrl, api_key: apiKey },
      };
    }
    default:
      return null;
  }
}

export function TargetForm({ onClose }: { onClose: () => void }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const { push } = useToast();
  const sites = useSites();
  const [form, setForm] = useState<FormState>(INITIAL);

  function set<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((current) => ({ ...current, [key]: value }));
  }

  const create = useMutation({
    mutationFn: (body: CreateDeliveryTargetRequest) =>
      api.post<DeliveryTarget>("/delivery/targets", body),
    onSuccess: (target) => {
      void queryClient.invalidateQueries({ queryKey: targetsKey(orgId) });
      push({
        tone: "success",
        title: "Target added",
        body: `${target.label} receives new contacts from now on. Use Test to send it a marked sample.`,
      });
      onClose();
    },
  });

  const body = buildCreateBody(form);
  const credentialsRefused =
    create.isError && isApiError(create.error) && create.error.token === "target_credentials_rejected";
  const recipientCount = parseRecipients(form.recipients).length;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add a delivery target"
      description="Where each new contact goes the moment Radar has it. Credentials are checked with the destination before anything is saved."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (body) create.mutate(body);
            }}
            loading={create.isPending}
            disabled={body === null}
          >
            Add target
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Select
          label="Destination"
          value={form.kind}
          onChange={(event) => set("kind", event.target.value as DeliveryTargetKind)}
        >
          {TARGET_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {TARGET_KIND_LABELS[kind]}
            </option>
          ))}
        </Select>

        <Input
          label="Label"
          value={form.label}
          onChange={(event) => set("label", event.target.value)}
          maxLength={80}
          placeholder="Sales inbox, HubSpot (marketing)…"
          hint="How this target is named in the list and in delivery history."
        />

        {form.kind === "email" ? (
          <Textarea
            label={`Recipients (up to ${MAX_RECIPIENTS})`}
            value={form.recipients}
            onChange={(event) => set("recipients", event.target.value)}
            placeholder={"sales@yourcompany.co.uk\nops@yourcompany.co.uk"}
            hint={`One address per line, or separated by commas. ${recipientCount} of ${MAX_RECIPIENTS} so far. Each contact arrives as one email with its card and a one-row CSV.`}
            {...(recipientCount > MAX_RECIPIENTS
              ? { error: `At most ${MAX_RECIPIENTS} addresses per target.` }
              : {})}
          />
        ) : null}

        {form.kind === "hubspot" ? (
          <>
            <Input
              label="Private app access token"
              type="password"
              autoComplete="off"
              value={form.accessToken}
              onChange={(event) => set("accessToken", event.target.value)}
              hint="Create one in HubSpot under Settings → Integrations → Private apps, and give it the crm.objects.contacts.write scope. Radar checks it with HubSpot before saving and stores it in its vault."
            />
            <Input
              label="Lifecycle stage (optional)"
              value={form.lifecycleStage}
              onChange={(event) => set("lifecycleStage", event.target.value)}
              maxLength={80}
              placeholder="lead"
              hint="Set on each contact Radar creates, if you use lifecycle stages."
            />
          </>
        ) : null}

        {form.kind === "pipedrive" ? (
          <>
            <Input
              label="API token"
              type="password"
              autoComplete="off"
              value={form.apiToken}
              onChange={(event) => set("apiToken", event.target.value)}
              hint="From Pipedrive: your profile menu, Personal preferences, API. Radar checks it before saving."
            />
            <Input
              label="Company domain"
              value={form.companyDomain}
              onChange={(event) => set("companyDomain", event.target.value)}
              placeholder="acme"
              hint="The subdomain of your Pipedrive account: acme for acme.pipedrive.com."
            />
          </>
        ) : null}

        {form.kind === "invrt_crm" || form.kind === "monti" ? (
          <>
            <Input
              label="Endpoint URL"
              type="url"
              value={form.endpointUrl}
              onChange={(event) => set("endpointUrl", event.target.value)}
              placeholder="https://"
              hint={`Must start with https://. Radar posts each contact there as signed JSON with an X-Radar-Signature header, the same scheme as webhooks. ${TARGET_KIND_LABELS[form.kind]} verifies the signature on its side.`}
            />
            <Input
              label="API key"
              type="password"
              autoComplete="off"
              value={form.apiKey}
              onChange={(event) => set("apiKey", event.target.value)}
              hint="Sent with every request so the destination knows it is you. Stored in Radar's vault, shown back only by its last characters."
            />
          </>
        ) : null}

        <Select
          label="Site scope"
          value={form.siteId}
          onChange={(event) => set("siteId", event.target.value)}
          hint={
            sites.isError
              ? "Your sites could not be loaded just now; the target applies to all of them until you edit it."
              : "Send contacts from every site, or from one site only."
          }
        >
          <option value="">All sites</option>
          {(sites.data?.sites ?? []).map((site) => (
            <option key={site.id} value={site.id}>
              {site.domain}
            </option>
          ))}
        </Select>

        <EventKindCheckboxes
          legend="Events to deliver"
          hint="Tick at least one."
          value={form.events}
          onChange={(events) => set("events", events)}
        />

        {credentialsRefused ? (
          <p role="alert" className="text-sm font-medium text-danger">
            {CREDENTIALS_REFUSED_COPY}
          </p>
        ) : create.isError ? (
          <ErrorState error={create.error} />
        ) : null}
      </div>
    </Dialog>
  );
}
